<template>
	<UserPageWrapper>
		<Title>{{ $t("seo.pages.my-applications.title") }}</Title>
		<Meta name="description" :content="$t('seo.pages.my-applications.description')" />
		<Link rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta property="og:title" :content="$t('seo.pages.my-applications.title')" />
		<Meta property="og:description" :content="$t('seo.pages.my-applications.description')" />
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />

		<AdaptiveContainer class="flex flex-col gap-0 mb-16">
			<BaseHeading type="h2" class="leading-3 mb-4">
				<span class="text-2xl md:text-3xl">{{ $t("user.applications.title") }}</span>
			</BaseHeading>

			<p>{{ $t("user.applications.info") }}</p>
		</AdaptiveContainer>

		<AdaptiveContainer>
			<template v-if="loading || !isAuth">
				<DjCardJob :loading="true" />
			</template>
			<template v-else-if="allApplications && isAuth">
				<CardJob
					v-for="(job, index) in allApplications"
					:key="index"
					:job="job"
					:showApplicationDate="true"
					:langs="langs"
					:locale="locale"
					:address="useRegionOverride.getAddress(job)"
					newTab
				/>
			</template>
		</AdaptiveContainer>
		<ResponsiveContainer class="flex mt-8">
			<BasePagination
				v-if="allApplications.length > 0 && !loading && paginationLastPage > 1"
				v-model="paginationPage"
				:total="paginationLastPage"
				class="mx-auto mb-10"
				@change-page="setPage"
				:next-lang="$t('paginationNext')"
				:prev-lang="$t('paginationPrev')"
			/>
		</ResponsiveContainer>

		<AdaptiveContainer class="pb-20">
			<p v-if="allApplications.length < 1 && !loading" class="italic">{{ $t("user.applications.noJobs") }}</p>
		</AdaptiveContainer>
	</UserPageWrapper>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { useRegionOverrides } from "@@/composables/regionOverrides";
import { ApplicationsRepository } from "~/repositories/ApplicationsRepository";
import type { Application } from "~/types/userObject";

export default defineComponent({
	name: "MyApplicationsPage",
	setup() {
		definePageMeta({
			middleware: ["auth"],
		});

		const applicationsRepository = new ApplicationsRepository();
		const useRegionOverride = useRegionOverrides();
		const { locale } = useI18n();
		const langs = useLangs.job;
		const loading = ref(true);
		const isAuth = useState("isAuth");

		const allApplications = ref<Application[]>([]);
		const paginationPage = ref(1);
		const paginationLastPage = ref(1);

		async function getApplications(page = 1) {
			loading.value = true;
			const response = await applicationsRepository.getApplications(page);
			allApplications.value = response.applications?.data ?? [];
			paginationLastPage.value = response.applications?.last_page ?? 1;
			paginationPage.value = response.applications?.current_page ?? 1;
			loading.value = false;
		}

		function setPage(page: number) {
			getApplications(page);
		}

		watch(isAuth, (val: boolean) => {
			val == true && getApplications();
		});

		onMounted(() => {
			isAuth.value == true && getApplications();
		});

		return {
			useRegionOverride,
			loading,
			langs,
			isAuth,
			locale,
			allApplications,
			paginationPage,
			paginationLastPage,
			setPage,
		};
	},
});
</script>
